
import { API_ENDPOINTS, getAuthHeaders, getToken } from '../config/api';
import { cacheSet, cacheGet, emitCacheUpdated } from '../utils/cache';
import { normalizeBootstrap } from './normalizeBootstrap';
import { readCoverage, writeCoverage, migrateLegacyLastFetch } from '../utils/cacheCoverage';
import { ensureCacheForUser } from '../utils/cacheUserGuard';
import { ensureRealtime } from '../realtime';

// Cache keys "Agenda"
const K_OPS_LIST       = 'ops/list';
const K_WEEKLY_HOURS   = 'ops/weeklyHours';
const K_OVERRIDES      = 'ops/overrides';
const K_OVERRIDES_META = 'ops/overrides:meta';

const K_LOGS_CURSOR    = 'agenda/logs/cursor';   // pour fallback polling

// Clés localStorage constantes
const EKEY = 'bootstrap:etag';
const CACHE_KEY = 'bootstrap-cache';

/**
 * Charge les données d'attendance sur 180 jours (6 mois)
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} Données d'attendance normalisées
 */
async function fetchActivityData(userId) {
  try {
    // Calculer la plage de 180 jours (de aujourd'hui - 90 jours à aujourd'hui + 90 jours = 6 mois)
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - 90);
    const toDate = new Date(today);
    toDate.setDate(today.getDate() + 90);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const from = formatDate(fromDate);
    const to = formatDate(toDate);

    console.log(`📊 [fetchActivityData] Chargement attendance 6 mois: ${from} → ${to}`);

    // Charger les opérateurs et l'attendance en parallèle
    const [operatorsRes, attendanceRes] = await Promise.all([
      fetch(API_ENDPOINTS.OPERATORS(userId), { 
        headers: getAuthHeaders(),
        credentials: 'include'
      }),
      fetch(API_ENDPOINTS.ATTENDANCE_DAYS(from, to, userId), { 
        headers: getAuthHeaders(),
        credentials: 'include'
      })
    ]);

    if (!operatorsRes.ok || !attendanceRes.ok) {
      console.warn('⚠️ [fetchActivityData] Erreur lors du chargement des données activity');
      return null;
    }

    const operators = await operatorsRes.json();
    const attendance = await attendanceRes.json();

    // Normaliser les opérateurs
    const normalizedOperators = operators.map(op => ({
      id: Number(op.id),
      name: op.name || `${op.first_name || ''} ${op.last_name || ''}`.trim() || `Opérateur #${op.id}`
    }));

    console.log(`✅ [fetchActivityData] Chargé ${normalizedOperators.length} opérateurs et ${attendance.length} enregistrements`);

  return {
    operators: normalizedOperators,
    attendance,
    lastFetch: {
      from,
      to,
      at: Date.now()
    }
  };
} catch (error) {
  console.error('❌ [fetchActivityData] Erreur:', error);
  return null;
}
}

// ---- Prime Tasks feed (first page) ----
async function primeTasksFeed(userId) {
  try {
    const headers = getAuthHeaders();
    const url = API_ENDPOINTS.TASKS_FEED({ user_id: userId, limit: 100 });
    const res = await fetch(url, { headers });
    if (!res.ok) return;
    const { items = [], next_cursor = null } = await res.json();
    // cache keys
    const KEY_LIST   = `tasks/list`;
    const KEY_CURSOR = `tasks/cursor`;
    // fusion + dédup (par id)
    const prev = cacheGet(KEY_LIST) || [];
    const seen = new Set(prev.map(x => x.id));
    const merged = prev.concat(items.filter(x => !seen.has(x.id)));
    cacheSet(KEY_LIST, merged);
    cacheSet(KEY_CURSOR, { next: next_cursor, ts: Date.now() });
    emitCacheUpdated('tasks/list');
  } catch(e) {
    console.warn('[bootstrap] primeTasksFeed error', e);
  }
}

// Helpers de reload ciblés
export async function reloadOpsListIfNeeded(token) {
  const res = await fetch(API_ENDPOINTS.OPS_LIST(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) return;
  const items = await res.json();
  cacheSet(K_OPS_LIST, Array.isArray(items) ? items : []);
  emitCacheUpdated(K_OPS_LIST); // 🔥 Fix: émettre le bon format
}

export async function reloadWeeklyHoursIfNeeded(token) {
  const res = await fetch(API_ENDPOINTS.OP_WEEKLY_HOURS(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) return;
  const data = await res.json(); // format attendu: { [opId]: { "1": {...}|null, ..., "7": {...}|null } }
  if (!data || typeof data !== 'object') return;
  cacheSet(K_WEEKLY_HOURS, data);
  emitCacheUpdated(K_WEEKLY_HOURS); // 🔥 Fix: émettre le bon format
}

// Charger les overrides pour 3 semaines (précédente + actuelle + suivante)
async function loadInitialOverrides(token) {
  const today = new Date();
  
  // Semaine précédente (lundi)
  const lastWeekStart = new Date(today);
  lastWeekStart.setDate(today.getDate() - today.getDay() - 6); // Lundi semaine précédente
  
  // Semaine suivante (dimanche)  
  const nextWeekEnd = new Date(today);
  nextWeekEnd.setDate(today.getDate() - today.getDay() + 13); // Dimanche semaine suivante
  
  const fromDate = lastWeekStart.toISOString().slice(0, 10);
  const toDate = nextWeekEnd.toISOString().slice(0, 10);
  
  console.log('🔄 [LoadInitialOverrides] Chargement 3 semaines:', fromDate, '→', toDate);
  
  const res = await fetch(API_ENDPOINTS.OP_OVERRIDES_BULK(fromDate, toDate), {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  
  if (!res.ok) {
    console.error('❌ [LoadInitialOverrides] Erreur chargement:', res.status);
    return;
  }
  
  const freshData = await res.json();
  console.log('✅ [LoadInitialOverrides] Données fraîches reçues:', freshData);
  
  // Remplacer complètement le cache
  cacheSet(K_OVERRIDES, freshData);
  cacheSet(K_OVERRIDES_META, {
    from: fromDate,
    to: toDate,
    lastUpdate: Date.now(),
    lastServerSync: Date.now()
  });
  
  emitCacheUpdated('ops/overrides');
  console.log('✅ [LoadInitialOverrides] Cache mis à jour avec données fraîches');
}
export async function reloadOverridesRangeIfNeeded(token) {
  const overridesMeta = cacheGet(K_OVERRIDES_META) || null;
  console.log('[ReloadOverridesRange] Meta from cache:', overridesMeta);
  if (!overridesMeta || !overridesMeta.from || !overridesMeta.to) {
    console.log('[ReloadOverridesRange] No visible range to reload (meta missing).');
    return;  // Agenda fera un bulk prochain rendu si absent
  }
  
  console.log('[ReloadOverridesRange] Fetching overrides for range:', overridesMeta.from, overridesMeta.to);
  console.log('[ReloadOverridesRange] 🔗 URL:', API_ENDPOINTS.OP_OVERRIDES_BULK(overridesMeta.from, overridesMeta.to));
  
  const res = await fetch(API_ENDPOINTS.OP_OVERRIDES_BULK(overridesMeta.from, overridesMeta.to), {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  
  if (!res.ok) {
    console.error('[ReloadOverridesRange] ❌ HTTP Error:', res.status);
    const errorText = await res.text();
    console.error('[ReloadOverridesRange] Error details:', errorText);
    return;
  }
  
  const obj = await res.json(); // { [opId]: Override[] }
  console.log('[ReloadOverridesRange] ✅ Données reçues serveur:', obj);
  if (!obj || typeof obj !== 'object') return;

  // 🔥 REMPLACEMENT COMPLET - Pas de merge avec cache obsolète
  console.log('[ReloadOverridesRange] 🔄 REMPLACEMENT COMPLET du cache par données serveur');
  
  // Directement utiliser les données du serveur (elles sont fraîches)
  const freshOverrides = {};
  for (const [oid, arr] of Object.entries(obj)) {
    const id = Number(oid);
    if (Array.isArray(arr) && arr.length > 0) {
      freshOverrides[id] = arr.map(o => {
        const date = String(o.date).includes('T') ? String(o.date).split('T')[0] : String(o.date);
        return {
          date,
          is_off: !!o.is_off,
          start_local: o.start_local ?? null,
          end_local: o.end_local ?? null,
          end_next_day: !!o.end_next_day,
          tz: o.tz ?? 'Europe/Paris',
          note: o.note ?? null
        };
      }).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    }
  }
  
  console.log('[ReloadOverridesRange] ✅ Cache remplacé par données serveur:', freshOverrides);
  cacheSet(K_OVERRIDES, freshOverrides);
  
  // Mettre à jour les métadonnées avec timestamp de sync serveur
  const currentMeta = cacheGet(K_OVERRIDES_META) || {};
  cacheSet(K_OVERRIDES_META, {
    ...currentMeta,
    lastUpdate: Date.now(),
    lastServerSync: Date.now() // Marquer la dernière sync serveur
  });
  
  emitCacheUpdated(K_OVERRIDES); // 🔥 Fix: émettre le bon format
  console.log('[ReloadOverridesRange] Cache updated for K_OVERRIDES, emitted event.');
}

// Suppression sélective d'un override
async function removeSpecificOverride(operatorId, date) {
  console.log('🔍 [RemoveSpecificOverride] Suppression override:', operatorId, date);
  const existing = cacheGet(K_OVERRIDES) || {};
  const opId = Number(operatorId);
  const dateStr = String(date).includes('T') ? String(date).split('T')[0] : String(date);
  
  console.log('🔍 [RemoveSpecificOverride] État du cache avant:', {
    operatorId: opId,
    dateStr,
    hasOperator: !!existing[opId],
    operatorOverrides: existing[opId]?.length || 0,
    allOperators: Object.keys(existing)
  });
  
  if (existing[opId] && Array.isArray(existing[opId])) {
    const beforeLength = existing[opId].length;
    const beforeOverrides = existing[opId].map(o => o.date);
    
    // Filtrer l'override à supprimer
    existing[opId] = existing[opId].filter(o => String(o.date) !== dateStr);
    
    if (existing[opId].length === 0) {
      delete existing[opId];
    }
    
    console.log('🔍 [RemoveSpecificOverride] État du cache après:', {
      operatorId: opId,
      beforeOverrides,
      afterLength: existing[opId]?.length || 0,
      removed: beforeLength > (existing[opId]?.length || 0)
    });
    
    // Mettre à jour le cache
    cacheSet(K_OVERRIDES, existing);
    emitCacheUpdated(K_OVERRIDES);
    
    console.log('✅ [RemoveSpecificOverride] Override supprimé:', opId, dateStr, `(${beforeLength} -> ${existing[opId]?.length || 0})`);
  } else {
    console.warn('❌ [RemoveSpecificOverride] Aucun override trouvé pour l\'opérateur:', opId);
    console.log('🔍 [RemoveSpecificOverride] Cache actuel:', existing);
  }
}

// Mise à jour sélective d'un override
async function updateSpecificOverride(operatorId, date, overrideData, token) {
  console.log('[UpdateSpecificOverride] Updating override:', operatorId, date, overrideData);
  
  // Récupérer l'override depuis l'API pour avoir les données complètes
  const res = await fetch(API_ENDPOINTS.OP_OVERRIDES_BULK(date, date), {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  
  if (!res.ok) {
    console.error('[UpdateSpecificOverride] Failed to fetch override data');
    return;
  }
  
  const apiData = await res.json();
  const opId = Number(operatorId);
  
  if (apiData[opId] && Array.isArray(apiData[opId])) {
    const existing = cacheGet(K_OVERRIDES) || {};
    const base = Array.isArray(existing[opId]) ? existing[opId] : [];
    const byDate = new Map(base.map(o => [String(o.date), o]));
    
    // Mettre à jour l'override pour cette date
    for (const o of apiData[opId]) {
      const dateStr = String(o.date).includes('T') ? String(o.date).split('T')[0] : String(o.date);
      byDate.set(dateStr, {
        date: dateStr,
        is_off: !!o.is_off,
        start_local: o.start_local ?? null,
        end_local: o.end_local ?? null,
        end_next_day: !!o.end_next_day,
        tz: o.tz ?? 'Europe/Paris',
        note: o.note ?? null,
      });
    }
    
    existing[opId] = Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    cacheSet(K_OVERRIDES, existing);
    emitCacheUpdated(K_OVERRIDES);
    console.log('[UpdateSpecificOverride] Override updated in cache:', opId, date);
  }
}

// déclenché par SSE events pour invalidation ciblée
// Version stable - traitement correct des suppressions d'overrides
export async function agendaSelectiveRefresh(evt) {
  console.log('🚨 [AgendaSelectiveRefresh] ===== FONCTION APPELÉE =====');
  console.log('🔥 [AgendaSelectiveRefresh] Appelé avec:', evt.type, evt);
  console.log('🔍 [AgendaSelectiveRefresh] Données complètes reçues:', JSON.stringify(evt, null, 2));
  
  // Forcer un alert pour être sûr qu'on voit l'appel
  if (evt.type && evt.type.includes('override')) {
    console.log('🚨🚨🚨 AGENDA SELECTIVE REFRESH APPELÉ POUR OVERRIDE 🚨🚨🚨');
  }
  
  const t = evt?.type || '';

  if (t.startsWith('op:override:')) {
    // Récupérer le cache AVANT modification pour debug
    const beforeCache = cacheGet('ops/overrides') || {};
    console.log('📦 [AgendaSelectiveRefresh] Cache AVANT modification:', JSON.stringify(beforeCache, null, 2));
    
    // Essayer différents formats de données possibles (backend + legacy)
    const operatorId = Number(
      evt.operator_id || evt.operatorId || evt.operator || 
      evt.data?.operator_id || evt.data?.operatorId ||
      evt.payload?.operator_id || evt.payload?.operatorId
    );
    const date = String(
      evt.date || evt.dateStr || evt.date_string || 
      evt.data?.date || evt.data?.dateStr ||
      evt.payload?.date || evt.payload?.dateStr || ''
    ).split('T')[0];
    
    console.log('🔍 [AgendaSelectiveRefresh] Données extraites:', { 
      operatorId, 
      date, 
      type: t,
      rawOperatorId: evt.operator_id || evt.operatorId || evt.operator,
      rawDate: evt.date || evt.dateStr || evt.date_string
    });
    
    if (!operatorId || !date || operatorId === 0 || date === '') {
      console.error('❌ [AgendaSelectiveRefresh] Données manquantes ou invalides:', { 
        operatorId, 
        date,
        evt: JSON.stringify(evt, null, 2)
      });
      return;
    }
    
    // Récupérer le cache actuel
    const currentOverrides = { ...beforeCache };
    
    if (t === 'op:override:delete') {
      console.log('🗑️ [AgendaSelectiveRefresh] SUPPRESSION override:', { operatorId, date });
      
      // Supprimer du cache
      if (currentOverrides[operatorId]) {
        const beforeLength = currentOverrides[operatorId].length;
        currentOverrides[operatorId] = currentOverrides[operatorId].filter(o => {
          const keepItem = o.date !== date;
          if (!keepItem) {
            console.log('🗑️ Suppression de l\'item:', o);
          }
          return keepItem;
        });
        const afterLength = currentOverrides[operatorId].length;
        
        console.log(`🗑️ Suppression: ${beforeLength} → ${afterLength} items pour opérateur ${operatorId}`);
        
        if (currentOverrides[operatorId].length === 0) {
          delete currentOverrides[operatorId];
          console.log('🗑️ Opérateur supprimé complètement (plus d\'overrides)');
        }
      } else {
        console.warn('⚠️ Tentative de suppression sur opérateur inexistant:', operatorId);
      }
      
    } else if (t === 'op:override:upsert') {
      console.log('✏️ [AgendaSelectiveRefresh] AJOUT/MODIFICATION override:', { operatorId, date });
      
      // Ajouter/modifier dans le cache
      if (!currentOverrides[operatorId]) {
        currentOverrides[operatorId] = [];
        console.log('✏️ Création nouvelle liste pour opérateur:', operatorId);
      }
      
      // Supprimer l'ancien si existe
      const beforeLength = currentOverrides[operatorId].length;
      currentOverrides[operatorId] = currentOverrides[operatorId].filter(o => o.date !== date);
      const afterFilterLength = currentOverrides[operatorId].length;
      
      if (beforeLength !== afterFilterLength) {
        console.log('✏️ Override existant supprimé pour remplacement');
      }
      
      // Ajouter le nouveau - extraire depuis différents formats
      const newOverride = {
        date: date,
        is_off: !!(
          evt.is_off || 
          evt.data?.is_off || 
          evt.payload?.is_off || 
          evt.payload?.override?.is_off
        ),
        start_local: (
          evt.start_local || 
          evt.data?.start_local || 
          evt.payload?.start_local || 
          evt.payload?.override?.start_local || 
          null
        ),
        end_local: (
          evt.end_local || 
          evt.data?.end_local || 
          evt.payload?.end_local || 
          evt.payload?.override?.end_local || 
          null
        ),
        end_next_day: !!(
          evt.end_next_day || 
          evt.data?.end_next_day || 
          evt.payload?.end_next_day || 
          evt.payload?.override?.end_next_day
        ),
        tz: (
          evt.tz || 
          evt.data?.tz || 
          evt.payload?.tz || 
          evt.payload?.override?.tz || 
          'Europe/Paris'
        ),
        note: (
          evt.note || 
          evt.data?.note || 
          evt.payload?.note || 
          evt.payload?.override?.note || 
          null
        )
      };
      
      currentOverrides[operatorId].push(newOverride);
      currentOverrides[operatorId].sort((a, b) => a.date.localeCompare(b.date));
      
      console.log('✏️ Nouvel override ajouté:', newOverride);
      console.log('✏️ Liste finale pour opérateur:', currentOverrides[operatorId]);
    }
    
    // Debug: comparer avant/après
    console.log('📦 [AgendaSelectiveRefresh] Cache APRÈS modification:', JSON.stringify(currentOverrides, null, 2));
    
    // Mettre à jour le cache avec logs détaillés
    console.log('💾 [AgendaSelectiveRefresh] Mise à jour du cache ops/overrides...');
    cacheSet('ops/overrides', currentOverrides);
    
    console.log('📡 [AgendaSelectiveRefresh] Émission événement cc:cache-updated...');
    emitCacheUpdated('ops/overrides');
    
    // Vérifier que le cache a bien été mis à jour
    const verifyCache = cacheGet('ops/overrides') || {};
    console.log('✅ [AgendaSelectiveRefresh] Vérification cache après mise à jour:', JSON.stringify(verifyCache, null, 2));
    
    return;
  }
  if (t === 'op:weekly_hours:update') {
    console.log('[AgendaSelectiveRefresh] WeeklyHours event detected, reloading weekly hours.');
    await reloadWeeklyHoursIfNeeded();
    return;
  }
  if (t === 'op:create' || t === 'op:update' || t === 'op:delete') {
    console.log('[AgendaSelectiveRefresh] Operator event detected, reloading operators list.');
    await reloadOpsListIfNeeded();
    return;
  }
  // Par défaut : pas d'action - SSE gère les mises à jour nécessaires
  console.log('[AgendaSelectiveRefresh] Événement non géré:', t);
}

export async function fetchBootstrap(userId, token) {
  // 🔒 Garde-fou : purger le cache si l'utilisateur a changé
  ensureCacheForUser(userId);
  
  // 🔥 CHARGER D'ABORD LES OVERRIDES FRAIS (3 semaines)
  console.log('🔄 [fetchBootstrap] Chargement initial overrides...');
  await loadInitialOverrides(token || getToken());
  
  // ✅ ETag backend corrigé - utilisation normale du cache
  
  const url = API_ENDPOINTS.BOOTSTRAP(userId);
  const prevETag = localStorage.getItem(EKEY) || undefined;
  
  // Debug minimal
  console.log('🔍 [Bootstrap] ETag:', prevETag);

  // Helper pour déposer toutes les sous-clés atomiques
  function depositToCache(data) {
    // Déposer chaque sous-ensemble dans le cache (clés stables)
    cacheSet('meta/generatedAt', data.generatedAt);

    cacheSet('tasks/list',     data.tasks.list);
    cacheSet('tasks/stats',    data.tasks.stats);
    cacheSet('tasks/weekly',   data.tasks.weekly);
    cacheSet('tasks/operators/day',   data.tasks.operators.day);
    cacheSet('tasks/operators/week',  data.tasks.operators.week);
    cacheSet('tasks/operators/month', data.tasks.operators.month);

    // 🔹 ce que tu avais déjà (steri/stats, steri/weekly, inventaire, etc.)
    cacheSet('steri/stats',        data.sterilization.stats);
    cacheSet('steri/weekly',       data.sterilization.weekly);
    cacheSet('steri/categories',   data.sterilization.categories);
    cacheSet('steri/operators',    data.sterilization.operators);
    cacheSet('steri/instruments',  data.sterilization.instruments);

    // 🔹 nouveaux blocs "ops"
    const ops = data.opsBundle || {};
    if (ops.ops?.list)        cacheSet('ops/list', ops.ops.list);
    if (ops.ops?.index)       cacheSet('operators/index', ops.ops.index); // on réutilise la clé existante côté Steri
    if (ops.weeklyHours)      cacheSet('ops/weeklyHours', ops.weeklyHours);
    if (ops.overrides) {
      // Merger avec le cache existant au lieu d'écraser
      const existingOverrides = cacheGet('ops/overrides') || {};
      const mergedOverrides = { ...existingOverrides };
      
      // Merger les nouvelles données
      for (const [opId, overrides] of Object.entries(ops.overrides)) {
        if (!mergedOverrides[opId]) {
          mergedOverrides[opId] = [];
        }
        
        // Créer une map pour déduplication par date
        const overrideMap = new Map();
        
        // Ajouter les existants
        mergedOverrides[opId].forEach(o => overrideMap.set(o.date, o));
        
        // Ajouter/écraser avec les nouveaux
        overrides.forEach(o => overrideMap.set(o.date, o));
        
        // Reconvertir en array trié
        mergedOverrides[opId] = Array.from(overrideMap.values())
          .sort((a, b) => a.date.localeCompare(b.date));
      }
      
      cacheSet('ops/overrides', mergedOverrides);
      
      // Mettre à jour la metadata intelligemment
      const existingMeta = cacheGet('ops/overrides:meta') || {};
      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() - 30);
      const toDate = new Date(today);
      toDate.setDate(today.getDate() + 30);
      const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      
      const newFrom = formatDate(fromDate);
      const newTo = formatDate(toDate);
      
      cacheSet('ops/overrides:meta', {
        from: !existingMeta.from || newFrom < existingMeta.from ? newFrom : existingMeta.from,
        to: !existingMeta.to || newTo > existingMeta.to ? newTo : existingMeta.to,
        lastUpdate: Date.now()
      });
    }
    if (ops.taskPrefs)        cacheSet('ops/taskPrefs', ops.taskPrefs);
    if (ops.series7w)         cacheSet('ops/series7w', ops.series7w);
    if (ops.series7m)         cacheSet('ops/series7m', ops.series7m);
    if (ops.kpiByOp)          cacheSet('ops/kpiByOp', ops.kpiByOp);
    if (ops.steriWindow)      cacheSet('ops/steriWindow', ops.steriWindow);
    if (ops.updatedAt)        cacheSet('ops/updatedAt', ops.updatedAt);

    // 🔹 legacy operators (pour compatibilité)
    cacheSet('operators/list',     data.operators?.list || []);
    cacheSet('operators/weekly-hours/by-id', data.operators?.weekly_hours_by_operator || {});
    cacheSet('operators/agg/day',   data.operators?.charts?.day   || null);
    cacheSet('operators/agg/week',  data.operators?.charts?.week  || null);
    cacheSet('operators/agg/month', data.operators?.charts?.month || null);
    cacheSet('operators/incidents/day',   data.operators?.incidents?.day   || { statsByOp:{}, lastByOp:{} });
    cacheSet('operators/incidents/week',  data.operators?.incidents?.week  || { statsByOp:{}, lastByOp:{} });
    cacheSet('operators/incidents/month', data.operators?.incidents?.month || { statsByOp:{}, lastByOp:{} });
    
    // 🔹 Activity data (attendance sur 60 jours)
    if (data.activity) {
      cacheSet('activity/operators', data.activity.operators || []);
      cacheSet('activity/attendance', data.activity.attendance || []);
      cacheSet('activity/lastFetch', data.activity.lastFetch || { from: null, to: null, at: Date.now() });
      
      // 🔹 Migration de lastFetch vers coverage v2
      const legacy = cacheGet('activity/lastFetch', null);
      const existingCov = cacheGet('activity/coverage', null);
      
      if (!existingCov && legacy) {
        console.log('🔄 [bootstrap] Migration legacy lastFetch → coverage v2');
        const cov = migrateLegacyLastFetch(legacy);
        cacheSet('activity/coverage', cov);
        emitCacheUpdated('activity/coverage');
      }
    }
  }

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...(prevETag ? { 'If-None-Match': prevETag } : {})
    },
  });

  // 304: utiliser le cache local
  if (res.status === 304) {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      // Charger les données d'activity en parallèle même si bootstrap est en cache
      const activityData = await fetchActivityData(userId);
      const parsedCache = JSON.parse(cached);
      if (activityData) {
        parsedCache.activity = activityData;
        depositToCache(parsedCache);
      }
      emitCacheUpdated('bootstrap');

      // 🚀 brancher le temps-réel une fois authentifié
      try { 
        console.log('🚀 [Bootstrap] Démarrage SSE temps réel...');
        await ensureRealtime(String(userId)); 
      } catch (error) {
        console.warn('⚠️ [Bootstrap] Erreur démarrage SSE:', error);
      }

      // Prime Tasks feed (non bloquant)
      primeTasksFeed(userId);

      return parsedCache;
    }
    // pas de cache ? on réessaie sans l'ETag
    const res2 = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
      }
    });
    
    // Valider res2 avant parse (blinder le fallback)
    if (!res2.ok) throw new Error(`Bootstrap fallback -> ${res2.status}`);
    const ct2 = res2.headers.get('content-type') || '';
    if (!ct2.includes('application/json')) {
      const text = await res2.text();
      throw new Error(`Bootstrap fallback non-JSON (${res2.status}). Début: ${text.slice(120)}…`);
    }
    
    const data2 = normalizeBootstrap(await res2.json());
    localStorage.setItem(CACHE_KEY, JSON.stringify(data2));
    const newTag2 = res2.headers.get('etag'); 
    if (newTag2) localStorage.setItem(EKEY, newTag2);

    // 🔹 Charger les données d'attendance
    const activityData2 = await fetchActivityData(userId);
    if (activityData2) {
      data2.activity = activityData2;
    }

    // Déposer les sous-clés atomiques (même logique que pour le 200)
    depositToCache(data2);
    emitCacheUpdated('bootstrap');

    // 🚀 brancher le temps-réel une fois authentifié
    try { 
      console.log('🚀 [Bootstrap] Démarrage SSE temps réel (fallback)...');
      await ensureRealtime(String(userId)); 
    } catch (error) {
      console.warn('⚠️ [Bootstrap] Erreur démarrage SSE (fallback):', error);
    }

    // Prime Tasks feed (non bloquant)
    primeTasksFeed(userId);

    return data2;
  }

  // Avant de parser, on vérifie que c'est bien du JSON
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text();
    throw new Error(
      `Bootstrap non-JSON (${res.status}). URL=${url}. Début: ${text.slice(0, 120)}…`
    );
  }

  const rawData = await res.json();
  const data = normalizeBootstrap(rawData);
  
  localStorage.setItem(CACHE_KEY, JSON.stringify(data));

  // Stocker l'ETag reçu
  const newETag = res.headers.get('etag');
  if (newETag) localStorage.setItem(EKEY, newETag);

  // 🔹 Charger les données d'attendance en parallèle (non bloquant)
  const activityData = await fetchActivityData(userId);
  if (activityData) {
    data.activity = activityData;
  }

  // Déposer les sous-clés atomiques via le helper
  depositToCache(data);
  emitCacheUpdated('bootstrap'); // un seul événement global

  // 🚀 brancher le temps-réel une fois authentifié
  try { 
    console.log('🚀 [Bootstrap] Démarrage SSE temps réel (fallback 2)...');
    await ensureRealtime(String(userId)); 
  } catch (error) {
    console.warn('⚠️ [Bootstrap] Erreur démarrage SSE (fallback 2):', error);
  }

  // Prime Tasks feed (non bloquant)
  primeTasksFeed(userId);

  return data;
}